import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@ais-ui/ui-core';
import React, { useState } from 'react';
import { CreateDetectorDialog } from './Mutations/CreateDetector';
import { detectorTypeToIcon } from './detectorToIcon';
import { DetectorType } from './Mutations/detectorForm';
import { useLocalization } from '@ais-ui/locale-management';
import { usePermissions } from '../../Permissions/PermissionsProvider';

const DetectorButton = ({
  id,
  Icon,
  onClick,
  text,
  disabled = false,
  tooltipContent,
}: {
  id: string;
  Icon: React.ElementType;
  onClick: () => void;
  text: string;
  disabled?: boolean;
  tooltipContent?: string;
}) => {
  const buttonElement = (
    <Button
      id={id}
      variant="ghost"
      onClick={onClick}
      className="flex gap-x-2 col-span-2 justify-start"
      disabled={disabled}
    >
      <Icon></Icon>
      {text}
    </Button>
  );

  // If disabled and has tooltip content, wrap in tooltip
  if (disabled && tooltipContent) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-block">{buttonElement}</span>
          </TooltipTrigger>
          <TooltipContent>
            <p>{tooltipContent}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return buttonElement;
};

export const DetectorActionsMenu = ({
  buttonText,
  className,
}: {
  buttonText: string;
  className?: string;
}) => {
  const { messages } = useLocalization();
  const [showDialog, setShowDialog] = useState(false);
  const [detectorType, setDetectorType] = useState<string>('');
  const [isOpen, setIsOpen] = useState(false);
  const permissions = usePermissions();

  const onStartCreate = (newDetectorType: string) => {
    setDetectorType(newDetectorType);
    setShowDialog(true);
  };

  return (
    <div className="flex w-full justify-end">
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button id="create-event-detector-button" className={className}>
            {buttonText}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80" side="left" align="end">
          <div className="grid gap-4">
            <div className="space-y-2">
              <h4 className="font-medium leading-none">
                {messages.eventdetectorsActionsMenuTitle}
              </h4>
              <p className="text-sm text-muted-foreground">
                {messages.eventdetectorsActionsMenuSubtitle}
              </p>
            </div>
            <div className="grid gap-2"></div>
          </div>
          <div className="grid grid-cols-4">
            <DetectorButton
              id="create-area-detector-button"
              Icon={detectorTypeToIcon('area')}
              onClick={() => onStartCreate('area')}
              text={messages.eventdetectorsActionsMenuAreaLabel}
            ></DetectorButton>
            <DetectorButton
              id="create-line-detector-button"
              Icon={detectorTypeToIcon('line')}
              onClick={() => onStartCreate('line')}
              text={messages.eventdetectorsActionsMenuLineLabel}
            ></DetectorButton>
            <DetectorButton
              id="create-cable-detector-button"
              Icon={detectorTypeToIcon('cable')}
              onClick={() => onStartCreate('cable')}
              text={messages.eventdetectorsActionsMenuCableLabel}
            ></DetectorButton>
            <DetectorButton
              id="create-grounding-detector-button"
              Icon={detectorTypeToIcon('grounding')}
              onClick={() => onStartCreate('grounding')}
              text={messages.eventdetectorsActionsMenuGroundingLabel}
              disabled={!permissions.grounding}
              tooltipContent={
                messages.eventdetectorsActionsMenuGroundingTooltip
              }
            ></DetectorButton>
          </div>
        </PopoverContent>
      </Popover>
      {showDialog && (
        <CreateDetectorDialog
          detectorType={detectorType as DetectorType}
          showDialog={showDialog}
          setShowDialog={setShowDialog}
        />
      )}
    </div>
  );
};
